/**
 * Stakeholder Engagement Page — Interactive drill-down
 * Click on any stakeholder card to see full details, engagement history, contacts
 */
import React, { useState } from 'react';
import { 
  MessageSquare, Users, Calendar, CheckCircle2, Clock, Plus, Search, Filter,
  Mail, Phone, X, ChevronRight, ArrowRight, MapPin, FileText, Star,
  Send, AlertCircle, History, Target, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const STAKEHOLDERS = [
  {
    id: 1, name: 'Investor Relations Group', type: 'investors', contacts: 12,
    lastEngagement: '2025-01-10', nextEngagement: '2025-02-15', status: 'active',
    topics: ['Climate Risk', 'ESG Strategy', 'Net Zero'],
    description: 'Primary investor group including institutional investors, pension funds, and ESG-focused asset managers.',
    contactList: [
      { name: 'Sarah Chen', role: 'Lead ESG Analyst', email: 'schen@investcorp.com', phone: '+44 20 7123 4567', lastContact: '2025-01-10' },
      { name: 'Marcus Webb', role: 'Portfolio Manager', email: 'mwebb@greenfund.eu', phone: '+49 30 8123 4567', lastContact: '2025-01-08' },
      { name: 'Emily Zhang', role: 'Head of Stewardship', email: 'ezhang@nordea-am.fi', phone: '+358 9 1234 567', lastContact: '2024-12-15' },
    ],
    engagements: [
      { date: '2025-01-10', type: 'meeting', title: 'Q4 2024 ESG Review', outcome: 'Positive — investors expressed satisfaction with TCFD alignment progress. Requested more detail on Scope 3 targets.', attendees: 8 },
      { date: '2024-11-15', type: 'report', title: 'Annual ESG Report Distribution', outcome: 'Distributed to 12 key institutional investors. 3 follow-up meetings requested.', attendees: 0 },
      { date: '2024-09-22', type: 'workshop', title: 'Climate Scenario Analysis Workshop', outcome: 'Presented 3 climate scenarios (1.5C, 2C, 3C). Investors agreed to support transition plan.', attendees: 6 },
      { date: '2024-06-01', type: 'meeting', title: 'H1 ESG Update', outcome: 'Discussed CSRD readiness. Board diversity improvements noted favorably.', attendees: 5 },
    ],
    materiality_topics: ['Climate change mitigation', 'Energy consumption', 'GHG emissions', 'Transition plan', 'Board oversight'],
    priority: 'critical',
  },
  {
    id: 2, name: 'Community Advisory Board', type: 'community', contacts: 8,
    lastEngagement: '2024-12-20', nextEngagement: '2025-03-01', status: 'scheduled',
    topics: ['Local Impact', 'Job Creation', 'Environmental'],
    description: 'Representatives from local communities near operational sites. Includes elected officials, community leaders, and environmental groups.',
    contactList: [
      { name: 'Tom Richards', role: 'Community Chair', email: 'trichards@localcounty.gov', phone: '+44 1234 567890', lastContact: '2024-12-20' },
      { name: 'Anita Rao', role: 'Environmental Officer', email: 'arao@greenwatch.org', phone: '+44 1234 567891', lastContact: '2024-12-20' },
    ],
    engagements: [
      { date: '2024-12-20', type: 'meeting', title: 'Q4 Community Update', outcome: 'Reviewed noise reduction measures and local hiring targets. Community satisfied with progress.', attendees: 8 },
      { date: '2024-09-15', type: 'site_visit', title: 'Factory Open Day', outcome: '45 community members visited. Positive feedback on waste reduction initiatives.', attendees: 45 },
      { date: '2024-06-10', type: 'workshop', title: 'Environmental Impact Workshop', outcome: 'Discussed planned expansion. Community raised water usage concerns — action plan agreed.', attendees: 12 },
    ],
    materiality_topics: ['Local communities', 'Water consumption', 'Biodiversity', 'Employment'],
    priority: 'high',
  },
  {
    id: 3, name: 'NGO Coalition', type: 'ngo', contacts: 5,
    lastEngagement: '2025-01-05', nextEngagement: '2025-01-30', status: 'active',
    topics: ['Human Rights', 'Supply Chain', 'Biodiversity'],
    description: 'Coalition of 5 NGOs focused on human rights in supply chains, biodiversity protection, and climate justice.',
    contactList: [
      { name: 'Clara Johansson', role: 'Campaign Director', email: 'cjohansson@hrwatch.org', phone: '+46 8 123 4567', lastContact: '2025-01-05' },
      { name: 'David Okafor', role: 'Supply Chain Analyst', email: 'dokafor@fairlabor.org', phone: '+1 212 555 0134', lastContact: '2024-12-18' },
    ],
    engagements: [
      { date: '2025-01-05', type: 'meeting', title: 'CSDDD Implementation Discussion', outcome: 'Reviewed due diligence procedures for tier 2-3 suppliers. NGOs requested more transparency on cobalt sourcing.', attendees: 5 },
      { date: '2024-10-20', type: 'report', title: 'Modern Slavery Statement Review', outcome: 'NGOs provided feedback on draft statement. 8 recommendations — 6 accepted.', attendees: 0 },
      { date: '2024-07-15', type: 'workshop', title: 'Biodiversity Impact Assessment', outcome: 'Joint review of biodiversity baseline. Agreed on monitoring methodology.', attendees: 7 },
    ],
    materiality_topics: ['Human rights due diligence', 'Value chain workers', 'Biodiversity impacts', 'Supply chain transparency'],
    priority: 'high',
  },
  {
    id: 4, name: 'Regulatory Bodies', type: 'regulator', contacts: 4,
    lastEngagement: '2024-11-15', nextEngagement: '2025-04-01', status: 'scheduled',
    topics: ['CSRD Compliance', 'CSDDD', 'Reporting'],
    description: 'National and EU regulatory authorities overseeing corporate sustainability reporting obligations.',
    contactList: [
      { name: 'Dr. Helena Fischer', role: 'CSRD Unit Lead', email: 'hfischer@bafin.de', phone: '+49 228 4108 0', lastContact: '2024-11-15' },
      { name: 'Pierre Moreau', role: 'Sustainability Reporting', email: 'pmoreau@amf-france.org', phone: '+33 1 5345 6060', lastContact: '2024-10-30' },
    ],
    engagements: [
      { date: '2024-11-15', type: 'meeting', title: 'CSRD Assurance Readiness Review', outcome: 'Regulator satisfied with limited assurance preparation. Recommended strengthening data quality controls.', attendees: 4 },
      { date: '2024-08-20', type: 'report', title: 'Regulatory Filing Submission', outcome: 'Filed annual sustainability statement. No material issues flagged.', attendees: 0 },
    ],
    materiality_topics: ['CSRD compliance', 'ESRS standards', 'Assurance readiness', 'Data quality'],
    priority: 'critical',
  },
  {
    id: 5, name: 'Employee Representatives Council', type: 'employee', contacts: 15,
    lastEngagement: '2025-01-12', nextEngagement: '2025-02-10', status: 'active',
    topics: ['Working Conditions', 'Diversity', 'Training', 'Health & Safety'],
    description: 'Works council and employee union representatives across all operating regions.',
    contactList: [
      { name: 'Anna Bergstrom', role: 'Works Council Chair', email: 'abergstrom@internal.com', phone: 'Ext 4501', lastContact: '2025-01-12' },
      { name: 'Rajesh Patel', role: 'Union Representative', email: 'rpatel@internal.com', phone: 'Ext 4502', lastContact: '2025-01-12' },
      { name: 'Maria Santos', role: 'D&I Committee Lead', email: 'msantos@internal.com', phone: 'Ext 4503', lastContact: '2025-01-08' },
    ],
    engagements: [
      { date: '2025-01-12', type: 'meeting', title: 'Q1 Social Metrics Review', outcome: 'Reviewed gender pay gap data (8.3%). Agreed on target reduction to 5% by 2026.', attendees: 12 },
      { date: '2024-11-22', type: 'workshop', title: 'Health & Safety Strategy', outcome: 'Presented new H&S management system. LTIR target set at <1.5 per million hours.', attendees: 15 },
      { date: '2024-09-05', type: 'meeting', title: 'Training Budget Discussion', outcome: 'Agreed to increase training budget by 15%. Focus on digital skills and sustainability literacy.', attendees: 8 },
    ],
    materiality_topics: ['Own workforce', 'Adequate wages', 'Training and development', 'Health and safety', 'Diversity'],
    priority: 'high',
  },
];

const TYPE_CONFIG = {
  investors: { label: 'Investors', bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  community: { label: 'Community', bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  ngo: { label: 'NGO', bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  regulator: { label: 'Regulator', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  employee: { label: 'Employee', bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  scheduled: { label: 'Scheduled', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  pending: { label: 'Pending', bg: 'bg-amber-500/20', text: 'text-amber-400' },
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', bg: 'bg-red-500/20', text: 'text-red-400' },
  high: { label: 'High', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  medium: { label: 'Medium', bg: 'bg-blue-500/20', text: 'text-blue-400' },
};

const ENGAGEMENT_TYPE_CONFIG = {
  meeting: { icon: Users, label: 'Meeting', color: 'text-emerald-400' },
  report: { icon: FileText, label: 'Report', color: 'text-blue-400' },
  workshop: { icon: Target, label: 'Workshop', color: 'text-purple-400' },
  site_visit: { icon: MapPin, label: 'Site Visit', color: 'text-amber-400' },
};

/* ── Detail Panel (slideout) ──────────────────────────────────────── */
function StakeholderDetailPanel({ stakeholder, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const typeConfig = TYPE_CONFIG[stakeholder.type];
  const statusConfig = STATUS_CONFIG[stakeholder.status];
  const priorityConfig = PRIORITY_CONFIG[stakeholder.priority] || PRIORITY_CONFIG.medium;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'history', label: 'Engagement History', icon: History },
    { id: 'topics', label: 'Material Topics', icon: Target },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="stakeholder-detail-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-[#0A0A0A] border-l border-[#1F1F1F] shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1F1F1F] p-6 z-10">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${typeConfig.bg} ${typeConfig.text}`}>
                  {typeConfig.label}
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${statusConfig.bg} ${statusConfig.text}`}>
                  {statusConfig.label}
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${priorityConfig.bg} ${priorityConfig.text}`}>
                  {priorityConfig.label} Priority
                </span>
              </div>
              <h2 className="text-xl font-bold text-white">{stakeholder.name}</h2>
              <p className="text-sm text-[#6B6B6B] mt-1">{stakeholder.description}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors" data-testid="close-detail-panel">
              <X size={20} />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F]'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={12} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-white">{stakeholder.contacts}</div>
                  <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-1">Contacts</div>
                </div>
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">{stakeholder.engagements?.length || 0}</div>
                  <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-1">Engagements</div>
                </div>
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400">{stakeholder.topics.length}</div>
                  <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-1">Topics</div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Calendar size={14} className="text-emerald-400" />
                  Engagement Schedule
                </h3>
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#6B6B6B]">Last Engagement</span>
                    <span className="text-sm text-white font-medium">{new Date(stakeholder.lastEngagement).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#6B6B6B]">Next Engagement</span>
                    <span className="text-sm text-emerald-400 font-medium">{new Date(stakeholder.nextEngagement).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#6B6B6B]">Days Until Next</span>
                    <span className="text-sm text-amber-400 font-medium">
                      {Math.max(0, Math.ceil((new Date(stakeholder.nextEngagement) - new Date()) / 86400000))} days
                    </span>
                  </div>
                </div>
              </div>

              {/* Recent Engagement */}
              {stakeholder.engagements?.[0] && (
                <div>
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <MessageSquare size={14} className="text-emerald-400" />
                    Latest Engagement
                  </h3>
                  <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-[#6B6B6B]">{new Date(stakeholder.engagements[0].date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className="px-2 py-0.5 text-[10px] rounded bg-emerald-500/20 text-emerald-400 capitalize">{stakeholder.engagements[0].type.replace('_', ' ')}</span>
                    </div>
                    <h4 className="text-sm font-medium text-white mb-2">{stakeholder.engagements[0].title}</h4>
                    <p className="text-xs text-[#A0A0A0] leading-relaxed">{stakeholder.engagements[0].outcome}</p>
                  </div>
                </div>
              )}

              {/* Discussion Topics */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Discussion Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {stakeholder.topics.map(topic => (
                    <span key={topic} className="px-3 py-1.5 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded-lg border border-[#2F2F2F] hover:border-emerald-500/30 hover:text-emerald-400 cursor-pointer transition-colors">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-3">
              {stakeholder.contactList?.map((contact, i) => (
                <div key={i} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-emerald-500/30 transition-colors" data-testid={`contact-card-${i}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-white">{contact.name}</h4>
                      <p className="text-xs text-[#6B6B6B] mt-0.5">{contact.role}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-[#6B6B6B]">Last Contact</div>
                      <div className="text-xs text-[#A0A0A0]">{new Date(contact.lastContact).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                      <Mail size={12} /> {contact.email}
                    </a>
                    <span className="flex items-center gap-1.5 text-xs text-[#A0A0A0]">
                      <Phone size={12} /> {contact.phone}
                    </span>
                  </div>
                </div>
              ))}
              {(!stakeholder.contactList || stakeholder.contactList.length === 0) && (
                <div className="text-center py-8 text-[#6B6B6B] text-sm">No contacts available</div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {stakeholder.engagements?.map((eng, i) => {
                const typeConf = ENGAGEMENT_TYPE_CONFIG[eng.type] || ENGAGEMENT_TYPE_CONFIG.meeting;
                const Icon = typeConf.icon;
                return (
                  <div key={i} className="relative pl-8" data-testid={`engagement-${i}`}>
                    {/* Timeline line */}
                    {i < stakeholder.engagements.length - 1 && (
                      <div className="absolute left-3 top-8 w-px h-[calc(100%+1rem)] bg-[#1F1F1F]" />
                    )}
                    {/* Timeline dot */}
                    <div className={`absolute left-0 top-1 p-1.5 rounded-full bg-[#111111] border border-[#1F1F1F]`}>
                      <Icon size={12} className={typeConf.color} />
                    </div>
                    {/* Content */}
                    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-emerald-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-[#6B6B6B]">
                          {new Date(eng.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className={`px-2 py-0.5 text-[10px] rounded bg-[#1F1F1F] ${typeConf.color} capitalize`}>
                          {typeConf.label}
                        </span>
                        {eng.attendees > 0 && (
                          <span className="text-[10px] text-[#6B6B6B] flex items-center gap-1">
                            <Users size={10} /> {eng.attendees}
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-medium text-white mb-2">{eng.title}</h4>
                      <p className="text-xs text-[#A0A0A0] leading-relaxed">{eng.outcome}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'topics' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Material Topics (ESRS-aligned)</h3>
                <div className="space-y-2">
                  {stakeholder.materiality_topics?.map((topic, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#111111] border border-[#1F1F1F] rounded-xl p-3 hover:border-emerald-500/30 transition-colors cursor-pointer" data-testid={`material-topic-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-sm text-white">{topic}</span>
                      </div>
                      <ChevronRight size={14} className="text-[#6B6B6B]" />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Discussion Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {stakeholder.topics.map(topic => (
                    <span key={topic} className="px-3 py-1.5 text-xs bg-emerald-500/10 text-emerald-400 rounded-lg border border-emerald-500/20">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-[#0A0A0A]/95 backdrop-blur-sm border-t border-[#1F1F1F] p-4 flex gap-3">
          <Button variant="outline" className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 border-emerald-500/30 text-emerald-400" data-testid="schedule-engagement-btn">
            <Calendar size={14} className="mr-2" /> Schedule Engagement
          </Button>
          <Button variant="outline" className="flex-1 bg-[#1F1F1F] hover:bg-[#2F2F2F] border-[#2F2F2F] text-white" data-testid="send-comms-btn">
            <Send size={14} className="mr-2" /> Send Communication
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ─────────────────────────────────────────────────────── */
export default function StakeholderEngagementPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);

  const filteredStakeholders = STAKEHOLDERS.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.topics.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === 'all' || s.type === filterType;
    return matchesSearch && matchesType;
  });

  const totalContacts = STAKEHOLDERS.reduce((sum, s) => sum + s.contacts, 0);
  const activeCount = STAKEHOLDERS.filter(s => s.status === 'active').length;
  const totalEngagements = STAKEHOLDERS.reduce((sum, s) => sum + (s.engagements?.length || 0), 0);

  return (
    <div className="space-y-6" data-testid="stakeholder-engagement-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Stakeholder Engagement</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Manage relationships and engagement with external stakeholders. Click any group to view full details.
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors" data-testid="add-stakeholder-btn">
          <Plus size={16} />
          <span className="text-sm font-medium">Add Group</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { icon: Users, value: STAKEHOLDERS.length, label: 'Stakeholder Groups', color: 'purple' },
          { icon: MessageSquare, value: activeCount, label: 'Active Engagements', color: 'emerald' },
          { icon: Users, value: totalContacts, label: 'Total Contacts', color: 'blue' },
          { icon: History, value: totalEngagements, label: 'Total Engagements', color: 'amber' },
        ].map(({ icon: Icon, value, label, color }) => (
          <div key={label} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-${color}-500/20`}>
                <Icon size={18} className={`text-${color}-400`} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${color === 'emerald' ? 'text-emerald-400' : 'text-white'}`}>{value}</div>
                <div className="text-xs text-[#6B6B6B]">{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search stakeholders or topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
            data-testid="stakeholder-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          {['all', 'investors', 'community', 'ngo', 'regulator', 'employee'].map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                filterType === type 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
              }`}
            >
              {type === 'all' ? 'All Types' : type}
            </button>
          ))}
        </div>
      </div>

      {/* Stakeholder Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredStakeholders.map(stakeholder => {
          const typeConfig = TYPE_CONFIG[stakeholder.type];
          const statusConfig = STATUS_CONFIG[stakeholder.status];
          const priorityConfig = PRIORITY_CONFIG[stakeholder.priority] || PRIORITY_CONFIG.medium;
          
          return (
            <div 
              key={stakeholder.id} 
              onClick={() => setSelectedStakeholder(stakeholder)}
              className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5 hover:border-emerald-500/30 transition-all cursor-pointer group"
              data-testid={`stakeholder-card-${stakeholder.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${typeConfig.bg} ${typeConfig.text}`}>
                      {typeConfig.label}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${statusConfig.bg} ${statusConfig.text}`}>
                      {statusConfig.label}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${priorityConfig.bg} ${priorityConfig.text}`}>
                      {priorityConfig.label}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-white group-hover:text-emerald-400 transition-colors">{stakeholder.name}</h3>
                  <p className="text-xs text-[#6B6B6B] mt-1 line-clamp-1">{stakeholder.description}</p>
                </div>
                <div className="flex items-center gap-1 text-[#6B6B6B] group-hover:text-emerald-400 transition-colors">
                  <span className="text-xs">View</span>
                  <ChevronRight size={14} />
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-[#6B6B6B] mb-3">
                <span className="flex items-center gap-1">
                  <Users size={10} /> {stakeholder.contacts} contacts
                </span>
                <span className="flex items-center gap-1">
                  <History size={10} /> {stakeholder.engagements?.length || 0} engagements
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={10} /> Last: {new Date(stakeholder.lastEngagement).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-3">
                {stakeholder.topics.map(topic => (
                  <span key={topic} className="px-2 py-0.5 text-[10px] bg-[#1F1F1F] text-[#A0A0A0] rounded">
                    {topic}
                  </span>
                ))}
              </div>

              {/* Latest engagement preview */}
              {stakeholder.engagements?.[0] && (
                <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#1A1A1A]">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare size={10} className="text-emerald-400" />
                    <span className="text-[10px] text-[#6B6B6B]">Latest: {stakeholder.engagements[0].title}</span>
                  </div>
                  <p className="text-[11px] text-[#A0A0A0] line-clamp-2">{stakeholder.engagements[0].outcome}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredStakeholders.length === 0 && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <Users size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No stakeholders found</h3>
          <p className="text-sm text-[#6B6B6B]">Add a new stakeholder group or adjust your filters</p>
        </div>
      )}

      {/* Detail Panel */}
      {selectedStakeholder && (
        <StakeholderDetailPanel
          stakeholder={selectedStakeholder}
          onClose={() => setSelectedStakeholder(null)}
        />
      )}
    </div>
  );
}
